import React, { useState } from 'react';
import { authPost } from '../../Utilities/Api';
import { toast } from 'react-toastify';

const UserRow = ({ user, currentUser, refresh }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        username: user.username,
        tokenBalance: user.tokenBalance || 0,
        echoBalance: user.echoBalance || 0,
        role: user.role
    });

    const isSelf = currentUser?._id === user._id;

    // ✅ Combined Save (Username, Tokens, Echoes, Role)
    const handleSave = async () => {
        try {
            // Ensure numbers are sent as numbers, not strings
            const payload = {
                ...editData,
                tokenBalance: Number(editData.tokenBalance),
                echoBalance: Number(editData.echoBalance)
            };

            const res = await authPost(`/admin/update-user/${user._id}`, payload);
            if (res.ok) {
                toast.success("User updated successfully");
                setIsEditing(false);
                refresh(); // 🔄 Refresh parent list to show new data
            } else {
                const errorData = await res.json();
                toast.error(errorData.message || "Update failed");
            }
        } catch (err) {
            toast.error("Failed to update user");
        }
    };

    const handleAction = async (actionType, payload = {}) => {
        try {
            const res = await authPost(`/admin/${actionType}/${user._id}`, payload);
            if (res.ok) {
                toast.success(`Action ${actionType} successful`);
                refresh();
            } else {
                const errorData = await res.json();
                toast.error(errorData.message || `Failed to ${actionType}`);
            }
        } catch (err) {
            toast.error(`System error during ${actionType}`);
        }
    };

    return (
        <tr className={user.isBanned ? 'row-banned' : ''}>
            {/* 👤 USERNAME & EMAIL */}
            <td>
                {isEditing ? (
                    <input
                        className="admin-edit-input"
                        value={editData.username}
                        onChange={e => setEditData({ ...editData, username: e.target.value })}
                        autoFocus
                    />
                ) : (
                    <div className="user-cell">
                        <span className="username">{user.username}</span>
                        <span className="email">{user.email}</span>
                        {isSelf && <span className="self-badge"> (You)</span>}
                    </div>
                )}
            </td>

            {/* 🛡️ ROLE SELECTION */}
            <td>
                {isEditing && currentUser.role === 'owner' && !isSelf ? (
                    <select
                        className="admin-role-select"
                        value={editData.role}
                        onChange={e => setEditData({ ...editData, role: e.target.value })}
                    >
                        <option value="user">User</option>
                        <option value="creator">Creator</option>
                        <option value="moderator">Moderator</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                    </select>
                ) : (
                    <span className={`role-badge ${user.role}`}>{user.role}</span>
                )}
            </td>

            {/* 🚦 STATUS BADGE */}
            <td>
                {user.isBanned ? (
                    <span className="status-tag banned">Banned</span>
                ) : user.muteUntil && new Date(user.muteUntil) > new Date() ? (
                    <span className="status-tag muted">Muted</span>
                ) : (
                    <span className="status-tag active">Active</span>
                )}
            </td>

            {/* 💰 BALANCES */}
            <td>
                {isEditing ? (
                    <div className="balance-edit-group">
                        <div className="input-with-label">
                            <span>T:</span>
                            <input type="number" value={editData.tokenBalance} onChange={e => setEditData({ ...editData, tokenBalance: e.target.value })} />
                        </div>
                        <div className="input-with-label">
                            <span>E:</span>
                            <input type="number" value={editData.echoBalance} onChange={e => setEditData({ ...editData, echoBalance: e.target.value })} />
                        </div>
                    </div>
                ) : (
                    <div className="balance-display">
                        <span className="tokens" title="Tokens">💰 {user.tokenBalance}</span>
                        <span className="echoes" title="Echoes">📣 {user.echoBalance}</span>
                    </div>
                )}
            </td>

            {/* ⚡ ACTIONS */}
            <td className="actions-cell">
                {isEditing ? (
                    <div className="action-btns">
                        <button onClick={handleSave} className="btn-save">Save</button>
                        <button onClick={() => { setIsEditing(false); setEditData({ ...user }); }} className="btn-cancel">Cancel</button>
                    </div>
                ) : (
                    <div className="action-btns">
                        <button onClick={() => setIsEditing(true)} className="btn-edit">Edit</button>
                        {!isSelf && (
                            <>
                                <button onClick={() => handleAction('ban', { isBanned: !user.isBanned })} className="btn-ban">
                                    {user.isBanned ? 'Unban' : 'Ban'}
                                </button>
                                <button onClick={() => {
                                    const reason = prompt(`Warning reason for ${user.username}:`);
                                    if (reason) handleAction('warn', { reason });
                                }} className="btn-warn">
                                    Warn ({user.warnings?.length || 0})
                                </button>
                            </>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
};

export default UserRow;